import tailwindStyles from '../styles/tailwind.css?inline';
import componentStyles from '../styles/component.css?inline';
import modalStyles from '../styles/modal.css?inline';

/**
 * Modern Product Gallery Component
 * Uses inline styles (Tailwind-inspired), Shadow DOM, and vanilla JavaScript
 * Replaces LightGallery with a custom modal solution
 */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    
    // DON'T create shadow root here - wait until we have data
    // this.attachShadow({ mode: 'open' });
    
    // Component state
    this.slides = [];
    this.currentIndex = 0;
    this.isModalOpen = false;
    this.config = {};
    this.eventListenersSetup = false;
    this.documentListenersSetup = false;
    this._externalModalEl = null; // For portal modal
    this._modalStylesInjected = false; // Track if modal styles are injected
    
    // Bind methods to preserve 'this' context
    this.handleKeydown = this.handleKeydown.bind(this);
    this._handleExternalModalClick = this._handleExternalModalClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.productId && this.dataset.cdnMediaUrl) {
      this._loadFromWorker();
    } else if (this.dataset.productId && this.dataset.cabicoreUrl) {
      this._loadFromCabiCore();
    } else {
      this._loadFromConfig();
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    this.removeEventListeners();
  }

  async _loadFromCabiCore() {
    try {
      const productId = this.dataset.productId;
      const cabicoreUrl = this.dataset.cabicoreUrl;
      const authToken = this.dataset.authToken;
      
      console.log('Loading media for product', productId, 'from CabiCore...');
      
      
      // Fetch product media from CabiCore
      const slides = await this._fetchProductMedia(cabicoreUrl, productId, authToken);
      
      if (slides.length === 0) {
        // No media found - fade in placeholder image
        console.log('No media found, showing placeholder image');
        this._showPlaceholder();
        return;
      }
      
      // Only NOW create the shadow DOM since we have data
      console.log('Creating shadow DOM with', slides.length, 'slides');
      this.attachShadow({ mode: 'open' });
      
      // Set up the gallery with fetched data
      this.config = { 
        mode: this.dataset.mode || "slide",
        slides: slides
      };
      this.slides = slides;
      this._initializeGallery();
      
    } catch (error) {
      console.error('Failed to load product media:', error);
      // Show placeholder image on API error
      console.log('API error, showing placeholder image');
      this._showPlaceholder();
    }
  }

  async _loadFromWorker() {
    try {
      const productId = this.dataset.productId;
      const cdnMediaUrl = this.dataset.cdnMediaUrl;

      const slides = await this._fetchFromWorkerShowcase(cdnMediaUrl, productId);

      if (slides.length === 0) {
        this._showPlaceholder();
        return;
      }

      this.attachShadow({ mode: 'open' });
      this.config = {
        mode: this.dataset.mode || "slide",
        slides: slides
      };
      this.slides = slides;
      this._initializeGallery();
    } catch (error) {
      console.error('Failed to load product media from worker:', error);
      // Fallback: try Directus if available
      if (this.dataset.cabicoreUrl) {
        console.log('Falling back to Directus...');
        this._loadFromCabiCore();
      } else {
        this._showPlaceholder();
      }
    }
  }

  _loadFromConfig() {
    this.config = JSON.parse(this.dataset.config || '{}');
    this.slides = this.config.slides || [];
    
    if (this.slides.length === 0) {
      // No slides in config - show placeholder image
      console.log('No slides in config, showing placeholder image');
      this._showPlaceholder();
      return;
    }
    
    // Only create shadow DOM if we have slides
    this.attachShadow({ mode: 'open' });
    this._initializeGallery();
  }

  async _fetchProductMedia(cabicoreUrl, productId, authToken) {
    const url = `${cabicoreUrl}/items/products_showcase_slider?filter[product][_eq]=${productId}&sort=sort&fields=*,file.*`;
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Only add auth header if token exists and isn't 'public'
    if (authToken && authToken !== 'public') {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      throw new Error(`CabiCore API error: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    // Map CabiCore data to our slide format
    return result.data.map(media => {
      if (media.file && media.file.cf_stream_uid) {
        // Video media with Cloudflare Stream UID
        return {
          type: 'video',
          src: media.file.cf_stream_uid,
          thumb: `https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${media.file.cf_stream_uid}/thumbnails/thumbnail.jpg?time=5s&height=52`,
          poster: `https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${media.file.cf_stream_uid}/thumbnails/thumbnail.jpg?time=5s&height=600`,
          alt: media.file.title || 'Product video',
          isCloudflareStream: true
        };
      } else if (media.file && media.file.type && media.file.type.startsWith('video/')) {
        // Video media with Cloudflare Stream UID
        return {
          type: 'video-reel',
          src: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=video,fit=scale-down,height=600,width=480,audio=false/https://cdn-alpha.cabinetparts.com/${media.file.filename_disk}`,
          thumb: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=frame,time=5s,fit=scale-down,height=90,width=72,audio=false/https://cdn-alpha.cabinetparts.com/${media.file.filename_disk}`,
          poster: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=frame,time=5s,fit=scale-down,height=600,width=480,audio=false/https://cdn-alpha.cabinetparts.com/${media.file.filename_disk}`,
          alt: media.file.title || 'Product video',
          isCloudflareStream: false
        };
      } else if (media.file) {
        // Image media
        const rotateParam = media.file.rotate_for_tall_aspect_ratio ? `&r=${media.file.rotate_for_tall_aspect_ratio}` : '';
        return {
          type: 'image',
          src: `https://cdn-media.cabinetparts.com/${media.file.filename_disk}?p=port-base${rotateParam}`,
          srcLarge: `https://cdn-media.cabinetparts.com/${media.file.filename_disk}?p=port-xl${rotateParam}`,
          thumb: `https://cdn-media.cabinetparts.com/${media.file.filename_disk}?p=sq-xs${rotateParam}`,
          alt: media.file.title || 'Product image'
        };
      }
      return null;
    }).filter(Boolean);
  }

  async _fetchFromWorkerShowcase(cdnMediaUrl, productId) {
    const response = await fetch(`${cdnMediaUrl}/api/showcase?id=${productId}`);
    if (!response.ok) {
      throw new Error(`Showcase API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    return (result.slides || []).map(slide => {
      if (slide.type === 'video' && slide.streamUid) {
        return {
          type: 'video',
          src: slide.streamUid,
          thumb: `https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${slide.streamUid}/thumbnails/thumbnail.jpg?time=5s&height=52`,
          poster: `https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${slide.streamUid}/thumbnails/thumbnail.jpg?time=5s&height=600`,
          alt: slide.title || 'Product video',
          isCloudflareStream: true
        };
      } else if (slide.type === 'video-reel') {
        return {
          type: 'video-reel',
          src: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=video,fit=scale-down,height=600,width=480,audio=false/https://cdn-alpha.cabinetparts.com/${slide.filename}`,
          thumb: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=frame,time=5s,fit=scale-down,height=90,width=72,audio=false/https://cdn-alpha.cabinetparts.com/${slide.filename}`,
          poster: `https://cdn-alpha.cabinetparts.com/cdn-cgi/media/mode=frame,time=5s,fit=scale-down,height=600,width=480,audio=false/https://cdn-alpha.cabinetparts.com/${slide.filename}`,
          alt: slide.title || 'Product video',
          isCloudflareStream: false
        };
      } else if (slide.type === 'image') {
        const rotateParam = slide.rotate ? `&r=${slide.rotate}` : '';
        return {
          type: 'image',
          src: `${cdnMediaUrl}/${slide.filename}?p=port-base${rotateParam}`,
          srcLarge: `${cdnMediaUrl}/${slide.filename}?p=port-xl${rotateParam}`,
          thumb: `${cdnMediaUrl}/${slide.filename}?p=sq-xs${rotateParam}`,
          alt: slide.title || 'Product image'
        };
      }
      return null;
    }).filter(Boolean);
  }

  _showPlaceholder() {
    const placeholder = this.querySelector('.gallery-placeholder');
    if (placeholder) {
      placeholder.classList.add('show');
    }
  }

  _initializeGallery() {
    this.render();
  }

  isMobile() {
    return window.innerWidth < 768; // Tailwind 'md' breakpoint
  }

  render() {
    if (!this.slides.length) return;

    const mainSlide = this.slides[this.currentIndex];
    const isMobile = this.isMobile();
    
    this.shadowRoot.innerHTML = `
      <style>${tailwindStyles}\n${componentStyles}</style>
      <div class="gallery-container flex ${isMobile ? 'flex-col' : 'gap-1'} max-w-4xl mx-auto opacity-0 animate-(--animate-fade-in)">
        ${!isMobile ? `
          <!-- Desktop Thumbnails (Left side) -->
          <div class="flex flex-col gap-2 w-[45px] max-h-96 overflow-x-hidden  overflow-y-auto">
            ${this.renderThumbnails()}
          </div>
        ` : ''}

        <!-- Main Content -->
        <div class="flex-1 ${isMobile ? 'mb-4' : 'max-w-lg'}">
          <div class="main-content-area aspect-4/5 bg-gray-50 rounded-xl overflow-hidden relative cursor-pointer">
            ${this.renderMainContent(mainSlide)}           
          </div>
        </div>

        ${isMobile ? `
          <!-- Mobile Thumbnails (Bottom) -->
          <div class="w-full">
            <div class="thumbnail-scroll flex gap-2 overflow-x-auto py-2 scrollbar-hide touch-pan-x">
              ${this.renderThumbnails(true)}
            </div>
          </div>
        ` : ''}
      </div>

      <!-- Modal for fullscreen view -->
      <div id="gallery-modal" class="fixed inset-0 bg-black/90 z-50 hidden items-center justify-center p-4 opacity-0 transition-opacity duration-300">
        <div class="relative w-full h-full flex items-center justify-center">
          <!-- Close button -->
          <button id="modal-close" class="absolute top-4 right-4 text-white bg-black/50 border-none rounded-full w-10 h-10 flex items-center justify-center cursor-pointer transition-all duration-200 z-10 scale-0 animate-pop-in [animation-delay:0.3s]">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          <!-- Navigation arrows -->
          ${this.slides.length > 1 ? `
            <button id="modal-prev" class="absolute left-4 top-1/2 -translate-y-1/2 -translate-x-5 text-white bg-black/50 border-none rounded-full w-12 h-12 flex items-center justify-center cursor-pointer transition-all duration-200 z-10 opacity-0 animate-slide-in-from-left [animation-delay:0.4s]">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            <button id="modal-next" class="absolute right-4 top-1/2 -translate-y-1/2 translate-x-5 text-white bg-black/50 border-none rounded-full w-12 h-12 flex items-center justify-center cursor-pointer transition-all duration-200 z-10 opacity-0 animate-slide-in-from-right [animation-delay:0.4s]">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          ` : ''}


        </div>
      </div>
      
      <style>
        /* Modal animations */
        #gallery-modal.modal-opening {
          display: flex !important;
          animation: modal-fade-in 0.3s ease-out forwards;
        }
        
        #gallery-modal.modal-closing {
          animation: modal-fade-out 0.2s ease-in forwards;
        }
      </style>
    `;
    
    // Add hover effects after rendering
    this.addHoverEffects();
    
    // Setup event listeners only once
    if (!this.eventListenersSetup) {
      setTimeout(() => {
        this.setupEventListeners();
        this.eventListenersSetup = true;
      }, 100);
    } else {
      // Just re-setup hover listeners for thumbnails when re-rendering
      setTimeout(() => {
        this.setupThumbnailHoverListeners();
      }, 100);
    }
  }

  addHoverEffects() {
    // Add hover effect for main content zoom indicator
    const mainContent = this.shadowRoot.querySelector('.main-content-area');
    const zoomIndicator = this.shadowRoot.querySelector('.zoom-indicator');
    
    if (mainContent && zoomIndicator) {
      mainContent.addEventListener('mouseenter', () => {
        zoomIndicator.style.opacity = '1';
      });
      
      mainContent.addEventListener('mouseleave', () => {
        zoomIndicator.style.opacity = '0';
      });
    }
  }

  renderThumbnails(isMobile = false) {
    return this.slides.map((slide, index) => `
      <div class="thumbnail-item cursor-pointer overflow-hidden transition-all duration-300 ${slide.type === 'video' ? 'relative' : ''}   ${isMobile ? '' : ''}"
           data-index="${index}">
        <img src="${slide.thumb}" 
             alt="${slide.alt}" 
             class="w-full h-full object-cover" 
             loading="lazy" />
        ${slide.type === 'video' ? `
          <div class="absolute inset-0 flex items-center justify-center  rounded-lg">
            <div class="w-6 h-6 bg-black/10 rounded-full flex items-center justify-center">
              <svg class="w-6 h-6 text-red-800 ml-0.3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 5v10l8-5-8-5z"/>
              </svg>
            </div>
          </div>
        ` : ''}
      </div>
    `).join('');
  }

  renderMainContent(slide) {
    
    if (slide.type === 'video') {
      if (slide.isCloudflareStream) {
        return `
          <iframe src="https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${slide.src}/iframe?poster=${encodeURI(slide.poster)}&preload=metadata&controls=true"
                  class="w-full h-full border-0 rounded-xl"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowfullscreen="true">
          </iframe>
        `;
      } else {
        return `
          <video controls class="w-full h-full object-cover rounded-xl" poster="${slide.poster}">
            <source src="${slide.src}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        `;
      }
    } else if (slide.type === 'video-reel') {
      return `
        <video autoplay muted loop playsinline class="w-full h-full object-cover rounded-xl" poster="${slide.poster}">
          <source src="${slide.src}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      `;
    } else {
      return `
        <img src="${slide.src}" 
             alt="${slide.alt}" 
             class="w-full h-full object-cover rounded-xl transition-transform duration-200  mix-blend-multiply bg-white" 
             loading="lazy" /> <!-- Zoom indicator for images -->
            
              <div class="zoom-indicator absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm opacity-0 transition-opacity duration-300">
                <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                </svg>
                Click to zoom
              </div>
      `;
    }
  }

  setupEventListeners() {
    // Add direct hover listeners to thumbnails after they're rendered
    this.setupThumbnailHoverListeners();

    // Use event delegation for all click interactions
    this.shadowRoot.addEventListener('click', (e) => {
      
      const thumbnailItem = e.target.closest('.thumbnail-item');
      if (thumbnailItem) {
        // console.log('Thumbnail clicked');
        const index = parseInt(thumbnailItem.dataset.index);
        this.currentIndex = index;
        this.updateActiveSlide(false); // Pass false to indicate this is not from thumbnail hover
        return;
      }

      // Main content click to open modal
      const mainContentArea = e.target.closest('.main-content-area');
      if (mainContentArea) {
        // console.log('Main content area clicked, target:', e.target.tagName);
        // Don't open modal if clicking on video controls
        if (e.target.tagName === 'VIDEO' || e.target.tagName === 'IFRAME') {
          // console.log('Ignoring click on video/iframe');
          return;
        }
        // console.log('Opening modal...');
        e.stopPropagation(); // Prevent event bubbling
        this.openModal();
        return;
      }

      // Modal controls
      if (e.target.closest('#modal-close')) {
        // console.log('Modal close button clicked');
        this.closeModal();
        return;
      }

      if (e.target.closest('#modal-prev')) {
        // console.log('Modal prev button clicked');
        this.previousSlide();
        return;
      }

      if (e.target.closest('#modal-next')) {
        // console.log('Modal next button clicked');
        this.nextSlide();
        return;
      }

      // Close modal on background click
      if (e.target.id === 'gallery-modal') {
        // console.log('Modal background clicked - closing modal');
        this.closeModal();
        return;
      }
      
      // Check if we're in modal and clicked somewhere else
      if (this.isModalOpen) {
        // console.log('Modal is open but click was not handled by any specific handler');
        // console.log('Checking if click was outside modal content...');
        
        const modalControls = e.target.closest('#modal-close, #modal-prev, #modal-next');
        
        // Allow closing on any click that's not on the navigation controls
        // This includes clicks on the modal content area (images/videos) and background
        if (!modalControls) {
          // console.log('Click was not on modal controls - closing modal');
          this.closeModal();
          return;
        } else {
          // console.log('Click was on modal controls - not closing');
        }
      }
    });

    // Only add document-level listeners once
    if (!this.documentListenersSetup) {
      // Keyboard navigation
      document.addEventListener('keydown', this.handleKeydown);

      // Handle responsive layout changes
      window.addEventListener('resize', () => {
        // Re-render if switching between mobile/desktop
        const wasMobile = this.shadowRoot.innerHTML.includes('thumbnail-scroll');
        const isMobile = this.isMobile();
        
        if ((wasMobile && !isMobile) || (!wasMobile && isMobile)) {
          this.render();
          this.addHoverEffects();
          // Re-setup thumbnail hover listeners after layout change
          setTimeout(() => {
            this.setupThumbnailHoverListeners();
          }, 100);
        }
      });
      
      this.documentListenersSetup = true;
    }
  }

  removeEventListeners() {
    // Remove the keydown listener
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Reset the flags
    this.documentListenersSetup = false;
    this.eventListenersSetup = false;
    
    // Note: Shadow DOM event listeners are automatically cleaned up when component is removed
  }

  handleKeydown(e) {
    if (!this.isModalOpen) return;

    switch (e.key) {
      case 'Escape':
        this.closeModal();
        break;
      case 'ArrowLeft':
        this.previousSlide();
        break;
      case 'ArrowRight':
        this.nextSlide();
        break;
    }
  }

  openModal() {
    // Remove any existing modal
    this.closeModal();
    
    // Inject modal styles into document head if not already done
    this._injectModalStyles();
    
    // Create external modal element
    const modal = document.createElement('div');
    modal.id = 'gallery-modal-portal';
    modal.className = 'gallery-modal-portal';
    modal.innerHTML = this._renderExternalModal();
    
    document.body.appendChild(modal);
    this._externalModalEl = modal;
    this.isModalOpen = true;
    document.body.style.overflow = 'hidden';
    
    // Attach event listeners
    modal.addEventListener('click', this._handleExternalModalClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  closeModal() {
    // Remove external modal if present
    if (this._externalModalEl) {
      this._externalModalEl.removeEventListener('click', this._handleExternalModalClick);
      document.body.removeChild(this._externalModalEl);
      this._externalModalEl = null;
    }
    this.isModalOpen = false;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleKeydown);
    
    // Note: We keep modal styles in the document head for performance 
    // since they might be needed again. They're small and don't hurt.
  }

  previousSlide() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this._updateExternalModal();
    this.updateActiveSlide(false);
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this._updateExternalModal();
    this.updateActiveSlide(false);
  }

  updateActiveSlide(fromThumbnailHover = false) {
    // Don't update active slide if modal is open - let modal handle its own updates
    if (this.isModalOpen) {
      // console.log('Skipping updateActiveSlide because modal is open');
      return;
    }
    
    // Update main content with fade transition
    const mainContentArea = this.shadowRoot.querySelector('.main-content-area');
    if (mainContentArea) {
      // Add fade out effect
      mainContentArea.classList.add('opacity-50', 'scale-[1]');
      
      setTimeout(() => {
        mainContentArea.innerHTML = this.renderMainContent(this.slides[this.currentIndex]);
        
        // Fade back in
        mainContentArea.classList.add('transition-all', 'duration-300');
        mainContentArea.classList.remove('opacity-50', 'scale-[1]');
        mainContentArea.classList.add('opacity-100', 'scale-100');
        
        // Always re-add hover effects after content change since innerHTML removes listeners
        this.addHoverEffects();
      }, 150);
    }

    // Update thumbnail active states with smooth transitions
    this.shadowRoot.querySelectorAll('.thumbnail-item').forEach((thumb, index) => {
      const isActive = index === this.currentIndex;
      
      // Update transform and opacity
      if (isActive) {
        thumb.classList.add('thumbnail-active');
      } else {
        thumb.classList.remove('thumbnail-active');
      }
    });

    // Auto-scroll active thumbnail into view on mobile
    if (this.isMobile()) {
      this.scrollToActiveThumbnail();
    }

    // Only re-add hover effects and listeners if this wasn't triggered by thumbnail hover
    // This prevents infinite re-binding of event listeners
    if (!fromThumbnailHover) {
      setTimeout(() => {
        this.addHoverEffects();
      }, 300);
    }
  }

  scrollToActiveThumbnail() {
    const activeThumb = this.shadowRoot.querySelector('.thumbnail-item[data-index="' + this.currentIndex + '"]');
    const scrollContainer = this.shadowRoot.querySelector('.thumbnail-scroll');
    
    if (activeThumb && scrollContainer) {
      const thumbRect = activeThumb.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const scrollLeft = activeThumb.offsetLeft - (containerRect.width / 2) + (thumbRect.width / 2);
      
      scrollContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }

  _updateExternalModal() {
    if (this._externalModalEl) {
      const modalContent = this._externalModalEl.querySelector('#modal-content');
      if (modalContent) {
        modalContent.innerHTML = this._renderExternalModalContent(this.slides[this.currentIndex]);
      }
    }
  }

  _handleExternalModalClick(e) {
    // Close on background click
    if (e.target.id === 'gallery-modal-portal') {
      this.closeModal();
      return;
    }
    
    // Modal controls
    if (e.target.closest('#modal-close')) {
      this.closeModal();
      return;
    }
    
    if (e.target.closest('#modal-prev')) {
      this.previousSlide();
      return;
    }
    
    if (e.target.closest('#modal-next')) {
      this.nextSlide();
      return;
    }
  }

  _renderExternalModal() {
    const mainSlide = this.slides[this.currentIndex];
    return `
      <div class="gallery-modal-container">
        <button id="modal-close" class="gallery-modal-close">
          <svg class="gallery-modal-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        ${this.slides.length > 1 ? `
          <button id="modal-prev" class="gallery-modal-prev">
            <svg class="gallery-modal-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <button id="modal-next" class="gallery-modal-next">
            <svg class="gallery-modal-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        ` : ''}
        <div id="modal-content" class="gallery-modal-content">
          ${this._renderExternalModalContent(mainSlide)}
        </div>
      </div>
    `;
  }

  _renderExternalModalContent(slide) {
    if (slide.type === 'video') {
      if (slide.isCloudflareStream) {
        return `
          <iframe src="https://customer-9bq2fbt71tgs2bys.cloudflarestream.com/${slide.src}/iframe?poster=${encodeURI(slide.poster)}&preload=metadata&controls=true"
                  class="gallery-modal-video"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowfullscreen="true">
          </iframe>
        `;
      } else {
        return `
          <video controls class="gallery-modal-video" poster="${slide.poster}" autoplay>
            <source src="${slide.src}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        `;
      }
    } else if (slide.type === 'video-reel') {
      return `
        <video autoplay muted loop playsinline class="w-full h-full object-cover rounded-xl" poster="${slide.poster}">
          <source src="${slide.src}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      `;
    } else {
      return `
        <img src="${slide.srcLarge || slide.src}" 
             alt="${slide.alt}" 
             class="gallery-modal-image" />
      `;
    }
  }

  _injectModalStyles() {
    if (this._modalStylesInjected) return;
    
    // Check if styles are already injected by another instance
    if (document.getElementById('gallery-modal-styles')) return;
    
    const styleElement = document.createElement('style');
    styleElement.id = 'gallery-modal-styles';
    styleElement.textContent = modalStyles;
    document.head.appendChild(styleElement);
    
    this._modalStylesInjected = true;
  }

  setupThumbnailHoverListeners() {
    // Only add hover listeners on desktop
    if (!this.isMobile()) {
      // Remove existing hover listeners first to prevent duplicates
      this.shadowRoot.querySelectorAll('.thumbnail-item').forEach((thumb) => {
        // Clone the element to remove all event listeners
        const newThumb = thumb.cloneNode(true);
        thumb.parentNode.replaceChild(newThumb, thumb);
      });
      
      // Now add fresh hover listeners
      this.shadowRoot.querySelectorAll('.thumbnail-item').forEach((thumb, index) => {
        thumb.addEventListener('mouseenter', () => {
          // Only change slide on hover if it's not the current slide
          if (index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateActiveSlide(true); // Pass true to indicate this is from thumbnail hover
          }
        });
      });
    }
  }
}

customElements.define('product-gallery', ProductGallery);
